import React from 'react';
import ReactDOM from 'react-dom';

const formatNumber = (n) => Number(n || 0).toLocaleString('id-ID');

const getInitials = (name) => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
};

const ProgramManagementCard = ({
  title,
  data,
  icon,
  baseColor = '#0d6efd',
  loading,
  batchLabel,
  unavailable,
}) => {
  const [showModal, setShowModal] = React.useState(null);
  const [modalSearch, setModalSearch] = React.useState('');
  const closeButtonRef = React.useRef(null);
  const searchInputRef = React.useRef(null);

  const {
    studentsCount,
    institutionsList: rawInstitutionsList,
    mentorsList: rawMentorsList,
  } = data || {};

  // Data lapangan bisa berisi entri null/kosong (mis. institusi belum ditetapkan
  // untuk peserta tertentu) — disaring dulu supaya count & modal tidak menghitung
  // "slot kosong" seolah-olah institusi/mentor sungguhan.
  const institutionsList = (rawInstitutionsList || []).filter(Boolean);
  const mentorsList = (rawMentorsList || []).filter(Boolean);
  const institutionsCount = institutionsList.length;
  const mentorsCount = mentorsList.length;

  const isEmpty =
    !loading && !unavailable && !studentsCount && !institutionsCount && !mentorsCount;

  const activeList = showModal === 'institutions' ? institutionsList : mentorsList;
  const modalTitle = showModal === 'institutions' ? 'Semua Institusi' : 'Semua Mentor';

  const sortedList = React.useMemo(
    () => [...(activeList || [])].sort((a, b) => a.localeCompare(b, 'id')),
    [activeList]
  );
  const filteredList = React.useMemo(() => {
    const q = modalSearch.trim().toLowerCase();
    if (!q) return sortedList;
    return sortedList.filter((item) => item.toLowerCase().includes(q));
  }, [sortedList, modalSearch]);

  const openModal = (type) => setShowModal(type);
  const closeModal = React.useCallback(() => {
    setShowModal(null);
    setModalSearch('');
  }, []);

  // Aksesibilitas: Escape untuk tutup, kunci scroll body selagi modal terbuka,
  // fokus otomatis ke search box (atau tombol close kalau list kosong) supaya
  // keyboard/screen-reader user tidak "kehilangan" konteks saat modal muncul.
  React.useEffect(() => {
    if (!showModal) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    (searchInputRef.current || closeButtonRef.current)?.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [showModal, closeModal]);

  return (
    <>
    <div className={`card border-0 shadow-sm rounded-4 overflow-hidden h-100 management-card-v2 ${unavailable ? 'card-unavailable' : ''}`}>
      <div style={{ height: 4, backgroundColor: unavailable ? '#d9d9d9' : baseColor }}></div>
      <div className='card-body p-3'>
        <div className='d-flex align-items-center gap-2 min-w-0 mb-1'>
          <div
            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: 32, height: 32, backgroundColor: `${baseColor}15`, color: baseColor }}
          >
            <i className={`bi ${icon} small`}></i>
          </div>
          <div className="min-w-0">
            <h6 className='fw-bold mb-0 text-dark lh-sm text-truncate' style={{ fontSize: '0.9rem' }} title={title}>{title}</h6>
            {batchLabel && (
              <div className="extra-small text-muted fw-semibold text-truncate">{batchLabel}</div>
            )}
          </div>
        </div>

        <div className="mt-3">
          {loading ? (
            <>
              <div className="skeleton-block rounded-3 mb-2" style={{ height: 56 }}></div>
              <div className="row g-2">
                <div className="col-6"><div className="skeleton-block rounded-3" style={{ height: 48 }}></div></div>
                <div className="col-6"><div className="skeleton-block rounded-3" style={{ height: 48 }}></div></div>
              </div>
            </>
          ) : unavailable ? (
            <div className="py-3 text-center">
              <i className="bi bi-calendar-x text-muted" style={{ fontSize: '1.25rem' }}></i>
              <div className="text-muted extra-small fw-semibold mt-1">Tidak ada batch program ini di periode terpilih</div>
            </div>
          ) : isEmpty ? (
            <div className="py-3 text-center">
              <i className="bi bi-inbox text-muted" style={{ fontSize: '1.25rem' }}></i>
              <div className="text-muted extra-small fw-semibold mt-1">Belum ada peserta di batch ini</div>
            </div>
          ) : (
            <>
              {/* Primary stat: Diterima (status Aktif) */}
              <div
                className="d-flex align-items-center justify-content-between rounded-3 px-3 py-2 mb-2"
                style={{ backgroundColor: `${baseColor}0d` }}
              >
                <span className="text-muted extra-small fw-bold text-uppercase">Diterima</span>
                <span className="fw-black h4 mb-0 text-nowrap" style={{ color: baseColor }}>{formatNumber(studentsCount)}</span>
              </div>

              {/* Secondary stats: Institusi & Mentor */}
              <div className="row g-2">
                <div className="col-6">
                  <button
                    type="button"
                    className="stat-tile-secondary stat-tile-clickable w-100 h-100 border-0 text-start position-relative"
                    onClick={() => openModal('institutions')}
                    disabled={institutionsCount === 0}
                    aria-haspopup="dialog"
                    aria-label={`Lihat ${institutionsCount} institusi`}
                  >
                    {institutionsCount > 0 && (
                      <i className="bi bi-box-arrow-up-right text-muted position-absolute" style={{ top: 10, right: 10, fontSize: '0.6rem' }}></i>
                    )}
                    <div className="text-muted extra-small fw-bold text-uppercase mb-1">Inst.</div>
                    <div className="fw-bold text-dark h5 mb-0">{formatNumber(institutionsCount)}</div>
                  </button>
                </div>
                <div className="col-6">
                  <button
                    type="button"
                    className="stat-tile-secondary stat-tile-clickable w-100 h-100 border-0 text-start position-relative"
                    onClick={() => openModal('mentors')}
                    disabled={mentorsCount === 0}
                    aria-haspopup="dialog"
                    aria-label={`Lihat ${mentorsCount} mentor`}
                  >
                    {mentorsCount > 0 && (
                      <i className="bi bi-box-arrow-up-right text-muted position-absolute" style={{ top: 10, right: 10, fontSize: '0.6rem' }}></i>
                    )}
                    <div className="text-muted extra-small fw-bold text-uppercase mb-1">Mentors</div>
                    <div className="fw-bold text-dark h5 mb-0">{formatNumber(mentorsCount)}</div>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        .min-w-0 { min-width: 0; }
        .stat-tile-secondary { background-color: #f8f9fa; border-radius: 12px; padding: 12px 14px; }
        .stat-tile-clickable { cursor: pointer; transition: background-color .15s ease, transform .15s ease; }
        .stat-tile-clickable:not(:disabled):hover { background-color: #eef2f7 !important; transform: translateY(-1px); }
        .stat-tile-clickable:focus-visible { outline: 2px solid ${baseColor}; outline-offset: 2px; }
        .stat-tile-clickable:disabled { cursor: default; opacity: 0.6; }
        .extra-small { font-size: 0.6rem; letter-spacing: 0.5px; }
        .fw-black { font-weight: 800; }
        .management-card-v2 { border: 1px solid #f0f0f0 !important; transition: box-shadow .2s ease, transform .2s ease; }
        .management-card-v2:hover { border-color: ${baseColor}44 !important; box-shadow: 0 10px 24px rgba(0,0,0,0.08) !important; transform: translateY(-2px); }
        .card-unavailable { opacity: 0.55; }
        .card-unavailable:hover { transform: none; box-shadow: none !important; }
        .skeleton-block { background: linear-gradient(90deg, #f0f0f0 25%, #f7f7f7 37%, #f0f0f0 63%); background-size: 400% 100%; animation: skeleton-loading 1.4s ease infinite; }
        @keyframes skeleton-loading { 0% { background-position: 100% 50%; } 100% { background-position: 0 50%; } }
      `}</style>
    </div>

    {/* Modal daftar lengkap institusi/mentor — di-portal ke document.body supaya tidak
        ke-clip oleh `overflow-hidden` kartu (kartu ini overflow-hidden untuk strip warna
        di atas & border-radius, tapi itu juga meng-clip modal kalau dirender sebagai
        anak DOM langsung, meski modal pakai position:fixed). */}
    {showModal && ReactDOM.createPortal(
      (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="pmc-modal-title"
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content border-0 rounded-4 shadow-lg">
              <div className="modal-header border-bottom p-4">
                <div className="d-flex align-items-center gap-3">
                  <div className="rounded-3 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, backgroundColor: `${baseColor}22`, color: baseColor }}>
                    <i className={`bi ${showModal === 'institutions' ? 'bi-building' : 'bi-person-badge'}`}></i>
                  </div>
                  <div>
                    <h5 className="modal-title fw-bold mb-0" id="pmc-modal-title">{modalTitle}</h5>
                    <span className="text-muted small">Program: {title}</span>
                  </div>
                </div>
                <button ref={closeButtonRef} type="button" className="btn-close" aria-label="Tutup" onClick={closeModal}></button>
              </div>
              <div className="modal-body p-0">
                <div className="d-flex align-items-center gap-2 px-4 pt-4 pb-3">
                  <div className="d-flex align-items-center gap-2 flex-grow-1 bg-light rounded-pill px-3 py-2">
                    <i className="bi bi-search text-muted"></i>
                    <input
                      ref={searchInputRef}
                      type="text"
                      className="form-control form-control-sm bg-light border-0 p-0 shadow-none"
                      placeholder={`Cari ${showModal === 'institutions' ? 'institusi' : 'mentor'}...`}
                      value={modalSearch}
                      onChange={(e) => setModalSearch(e.target.value)}
                    />
                  </div>
                  <span className="badge bg-light text-dark border fw-medium px-3 py-2 rounded-pill flex-shrink-0">
                    {filteredList.length}{modalSearch ? ` / ${sortedList.length}` : ''}
                  </span>
                </div>
                {filteredList.length === 0 ? (
                  <div className="text-center text-muted small py-5">
                    <i className="bi bi-search fs-4 d-block mb-2 opacity-50"></i>
                    Tidak ada yang cocok dengan "{modalSearch}"
                  </div>
                ) : (
                  <div className="list-group list-group-flush border-top">
                    {filteredList.map((item, idx) => (
                      <div key={idx} className="list-group-item border-0 px-4 py-2 d-flex align-items-center gap-3 hover-bg-light">
                        <span
                          className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 fw-bold"
                          style={{ width: 30, height: 30, fontSize: '0.65rem', backgroundColor: `${baseColor}15`, color: baseColor }}
                        >
                          {showModal === 'institutions' ? <i className="bi bi-building" style={{ fontSize: '0.75rem' }}></i> : getInitials(item)}
                        </span>
                        <span className="fw-medium text-dark small">{item}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-footer border-0 p-4 pt-2">
                <button type="button" className="btn btn-light rounded-pill px-4 fw-bold" onClick={closeModal}>Tutup</button>
              </div>
            </div>
          </div>
        </div>
      ),
      document.body
    )}
    </>
  );
};

export default ProgramManagementCard;
